import './duplicateApplicationPage.scss';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import linkIcon from '../../assets/link-icon.svg';
import { partnerBadgeAssets } from '../../components/ApplicationCard/constants/partnerBadgeAssets';
import { CircularProgressFullScreen } from '../../components/CircularProgressFullScreen/CircularProgressFullScreen';
import { Header } from '../../components/Header/Header';
import useDashboardStore from '../../store/dashboardStore/useDashboardStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    activeBrand,
    duplicateBrand,
    vehicleId: parameterVehicleId,
    clearHasDuplicateApplication,
  } = useParameterStore();

  const {
    computed: { vehicleYearMakeModel },
    isFetchingVehicleById,
    loadCurrentVehicle,
  } = useDashboardStore();

  // the duplicate brand is only sent when it differs from the active brand, so
  // its presence is what selects the brand change variation of this page
  const isBrandChange = isNonEmptyString(duplicateBrand);

  // falls back to a generic description if the vehicle fetch below fails - nothing
  // else on this page depends on the vehicle having resolved
  const vehicleDescription =
    vehicleYearMakeModel || t('DuplicateApplicationPage.defaultVehicle');

  const accountClicked = (): void => {
    clearHasDuplicateApplication();

    navigate('/dashboard/garage');
  };

  useEffect((): void => {
    loadCurrentVehicle(parameterVehicleId);
  }, []);

  if (isFetchingVehicleById) {
    return <CircularProgressFullScreen />;
  }

  return (
    <div className="duplicate-application-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header showDefaultLogo showLogo />

          <main data-testid={locator.pages.duplicateApplication.main.root}>
            <div className="badges">
              {isBrandChange && (
                <span className="badge">
                  <img
                    alt={duplicateBrand}
                    data-testid={
                      locator.pages.duplicateApplication.main
                        .duplicateBrandBadge
                    }
                    src={partnerBadgeAssets[duplicateBrand]}
                  />
                </span>
              )}

              <span className="badge">
                <img
                  alt={activeBrand}
                  data-testid={
                    locator.pages.duplicateApplication.main.activeBrandBadge
                  }
                  src={partnerBadgeAssets[activeBrand]}
                />
              </span>

              {isBrandChange && (
                <span className="link-icon">
                  <img
                    alt=""
                    data-testid={
                      locator.pages.duplicateApplication.main.linkIcon
                    }
                    src={linkIcon}
                  />
                </span>
              )}
            </div>

            <h1 data-testid={locator.pages.duplicateApplication.main.h1}>
              {isBrandChange
                ? t('DuplicateApplicationPage.brandChangeTitle')
                : t('DuplicateApplicationPage.title')}
            </h1>

            <h2 data-testid={locator.pages.duplicateApplication.main.h2}>
              {isBrandChange
                ? t('DuplicateApplicationPage.brandChangeSubtitle', {
                    activeBrand,
                    duplicateBrand,
                    vehicle: vehicleDescription,
                  })
                : t('DuplicateApplicationPage.subtitle', {
                    vehicle: vehicleDescription,
                  })}
            </h2>

            <Button
              color="primary"
              data-testid={
                locator.pages.duplicateApplication.main.accountButton
              }
              type="button"
              variant="contained"
              fullWidth
              onClick={accountClicked}
            >
              {t('DuplicateApplicationPage.account')}
            </Button>

            <p
              className="notice"
              data-testid={locator.pages.duplicateApplication.main.notice}
            >
              {t('DuplicateApplicationPage.notice')}
            </p>
          </main>

          <footer data-testid={locator.pages.duplicateApplication.footer.root}>
            {t('copyright', { year: new Date().getFullYear() })}
          </footer>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'DuplicateApplicationPage';
